import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { NavBar } from '@/components/NavBar';
import { Footer } from '@/components/Footer';
import { ExploreMoreSection } from '@/components/ExploreMoreSection';
import { LeadForm } from '@/components/LeadForm';
import { MeetingModal } from '@/components/MeetingModal';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card';
import { Linkedin, Mail, Sparkles, HeartHandshake, Leaf, Star, Info } from 'lucide-react';
import { teamMembers, partners } from '@/data/team';
import { trackEvent } from '@/lib/analytics';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';

export const About = () => {
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const handleWhatsAppClick = () => {
    trackEvent('whatsapp_click', { location: 'about_page' });
    window.open('https://wa.me/13451234567?text=Hello, I would like to learn more about The Pearl at Grand Harbour.', '_blank');
  };

  const coreValues = [
    {
      icon: Sparkles,
      title: 'Innovation',
      description: 'Pioneering new standards in Caribbean luxury development',
    },
    {
      icon: HeartHandshake,
      title: 'Community',
      description: 'Building spaces that bring people together',
    },
    {
      icon: Leaf,
      title: 'Sustainability',
      description: 'Committed to environmental responsibility',
    },
    {
      icon: Star,
      title: 'Excellence',
      description: 'Uncompromising quality in every detail',
    },
  ];

  const milestones = [
    { year: '2022', title: 'Project Conception', description: 'Vision for The Pearl established' },
    { year: '2023', title: 'Planning & Approvals', description: 'Secured permits and partnerships' },
    { year: '2024', title: 'Construction Begins', description: 'Groundbreaking ceremony completed' },
    { year: '2028', title: 'Projected Completion', description: 'First residents move in' },
  ];

  return (
    <>
      <Helmet>
        <title>About Us - The Pearl at Grand Harbour | Luxury Development Team</title>
        <meta 
          name="description" 
          content="Meet the team behind The Pearl at Grand Harbour. Learn about our vision, values, and commitment to creating exceptional lagoon-side living in Grand Cayman." 
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <NavBar 
          onRegisterClick={() => setShowLeadForm(true)}
          onConsultClick={() => setShowMeetingModal(true)}
        />

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-deep-navy via-midnight-blue to-deep-navy opacity-95" />
          <div className="absolute inset-0 water-ripple" />
          
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <nav className="flex justify-center mb-6" aria-label="Breadcrumb">
                <ol className="inline-flex items-center space-x-2 text-sm text-white/60">
                  <li><a href="/" className="hover:text-brand-teal transition-colors">Home</a></li>
                  <li className="flex items-center"><span className="mx-2">/</span>About Us</li>
                </ol>
              </nav>
              
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6">
                Building Grand Harbour's Future
              </h1>
              <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
                Meet the team bringing The Pearl to life—a group of passionate professionals committed to creating exceptional lagoon-side living
              </p>
            </div>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                At The Pearl, we're not just building residences—we're creating a vibrant community where modern luxury meets Caribbean charm. Our commitment to excellence, sustainability, and community-focused design sets us apart in the Grand Harbour landscape.
              </p>
            </div>

            {/* Core Values */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {coreValues.map((value) => (
                <Card key={value.title} className="hover-lift text-center">
                  <CardContent className="pt-8 pb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-4">
                      <value.icon className="w-8 h-8 text-accent" />
                    </div>
                    <h3 className="font-display text-xl font-semibold mb-3">{value.title}</h3>
                    <p className="text-sm text-muted-foreground">{value.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Leadership Team */}
        <section className="py-16 lg:py-24 bg-soft-cream">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4">
                Leadership Team
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Experienced professionals dedicated to bringing The Pearl vision to life
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member) => (
                <Card key={member.id} className="hover-lift">
                  <CardContent className="pt-8">
                    <div className="text-center mb-6">
                      <Avatar className="w-24 h-24 mx-auto mb-4 border-4 border-accent/20">
                        <AvatarFallback className="bg-accent/10 text-accent text-2xl font-semibold">
                          {member.initials}
                        </AvatarFallback>
                      </Avatar>
                      <h3 className="font-display text-xl font-semibold mb-1">{member.name}</h3>
                      <p className="text-sm text-accent font-medium mb-4">{member.title}</p>
                    </div>
                    
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                      {member.bio}
                    </p>

                    <div className="flex items-center justify-center gap-3 pt-4 border-t">
                      {member.linkedIn && (
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          onClick={() => trackEvent('team_linkedin_click', { member: member.name })}
                        >
                          <a href={member.linkedIn} target="_blank" rel="noopener noreferrer" aria-label={`${member.name}'s LinkedIn`}>
                            <Linkedin className="w-4 h-4" />
                          </a>
                        </Button>
                      )}
                      {member.email && (
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          onClick={() => trackEvent('team_email_click', { member: member.name })}
                        >
                          <a href={`mailto:${member.email}`} aria-label={`Email ${member.name}`}>
                            <Mail className="w-4 h-4" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Partners Section */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4">
                Our Trusted Partners
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Collaborating with industry leaders to deliver excellence
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-12 lg:gap-16">
              {partners.map((partner) => {
                const logoContent = partner.logo ? (
                  <div className="flex items-center justify-center relative">
                    <img 
                      src={partner.logo} 
                      alt={partner.name}
                      className={`max-w-[320px] max-h-32 object-contain transition-all duration-300 ${partner.description ? 'cursor-pointer' : partner.url ? 'cursor-pointer' : ''}`}
                    />
                    {partner.description && (
                      <div className="absolute -top-1 -right-1 bg-accent/90 hover:bg-accent rounded-full p-1 shadow-sm transition-colors group-hover:scale-110">
                        <Info className="w-3 h-3 text-accent-foreground" />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Building2 className="w-8 h-8 text-accent" />
                  </div>
                );

                if (partner.description) {
                  return (
                    <div key={partner.id} className="flex flex-col items-center justify-center">
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <div className="flex flex-col items-center justify-center cursor-pointer group">
                            {logoContent}
                            {isMobile && (
                              <p className="text-xs text-muted-foreground mt-2 opacity-70">
                                Tap for details
                              </p>
                            )}
                          </div>
                        </HoverCardTrigger>
                        <HoverCardContent 
                          className={`${isMobile ? 'w-[90vw] max-w-sm' : 'w-80'} p-4`}
                          side={isMobile ? 'bottom' : 'top'}
                          align="center"
                        >
                          <p className="text-sm leading-relaxed">{partner.description}</p>
                          {partner.url && (
                            <a
                              href={partner.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block mt-3 text-xs text-accent hover:underline font-medium"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Visit website →
                            </a>
                          )}
                        </HoverCardContent>
                      </HoverCard>
                    </div>
                  );
                }

                return (
                  <div 
                    key={partner.id} 
                    className="flex flex-col items-center justify-center"
                  >
                    {partner.url ? (
                      <a 
                        href={partner.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block"
                      >
                        {logoContent}
                      </a>
                    ) : (
                      logoContent
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Timeline */}
        <section className="py-16 lg:py-24 bg-soft-cream">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-4">
                Our Journey
              </h2>
              <p className="text-lg text-muted-foreground">
                Key milestones in bringing The Pearl to life
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-accent/30 lg:left-1/2 lg:-translate-x-px" />

                <div className="space-y-12">
                  {milestones.map((milestone, index) => (
                    <div key={milestone.year} className="relative flex items-start gap-6 lg:gap-12">
                      {/* Timeline dot */}
                      <div className="absolute left-8 w-4 h-4 rounded-full bg-accent border-4 border-background lg:left-1/2 lg:-translate-x-1/2" />
                      
                      <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right lg:pr-16' : 'lg:pl-16 lg:ml-auto'} pl-16 lg:w-1/2`}>
                        <Card className="hover-lift">
                          <CardContent className="p-6">
                            <div className="inline-block px-3 py-1 rounded-full bg-accent/10 text-accent font-semibold text-sm mb-3">
                              {milestone.year}
                            </div>
                            <h3 className="font-display text-xl font-semibold mb-2">{milestone.title}</h3>
                            <p className="text-muted-foreground">{milestone.description}</p>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-24 bg-gradient-to-br from-deep-navy via-midnight-blue to-deep-navy text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-3xl md:text-4xl font-semibold mb-6">
              Want to Learn More?
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto mb-10">
              Connect with our team to discover how The Pearl can be your perfect home
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg" 
                className="shadow-luxury"
                onClick={() => {
                  trackEvent('cta_click', { location: 'about_page', action: 'register' });
                  setShowLeadForm(true);
                }}
              >
                Register Interest
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                onClick={() => {
                  trackEvent('cta_click', { location: 'about_page', action: 'book_consult' });
                  setShowMeetingModal(true);
                }}
              >
                Book Consultation
              </Button>
              <Button 
                size="lg" 
                variant="ghost"
                className="text-white hover:bg-white/10"
                onClick={() => {
                  trackEvent('cta_click', { location: 'about_page', action: 'view_properties' });
                  navigate('/residences');
                }}
              >
                View Properties
              </Button>
            </div>
          </div>
        </section>

        {/* Explore More Section */}
        <ExploreMoreSection 
          onContactClick={() => setShowLeadForm(true)}
        />

        <Footer />

        {/* Modals */}
        <LeadForm open={showLeadForm} onOpenChange={setShowLeadForm} />
        <MeetingModal 
          open={showMeetingModal} 
          onOpenChange={setShowMeetingModal}
          onWhatsAppClick={handleWhatsAppClick}
        />
        <WhatsAppButton onClick={handleWhatsAppClick} />
      </div>
    </>
  );
};
